import type { User } from "~/features/session/schemas";
import type {
  AppConfig,
  FeatureFlagGroup,
  FeatureFlagPath,
} from "../../../app-config-schema";
import { isFeatureFlag, isFeatureFlagPath } from "../../../app-config-schema";

import type { CreateAbility, ForcedSubject, MongoAbility } from "@casl/ability";
import { AbilityBuilder, createMongoAbility } from "@casl/ability";

type Action = "use";

type AppAbilities = [
  Action,
  FeatureFlagPath | ForcedSubject<Exclude<FeatureFlagPath, "all">>,
];

export type AppAbility = MongoAbility<AppAbilities>;

/**
 * Utility function to check if feature is enabled for user.
 * @param appConfig AppConfig
 * @param featurePath featurePath we are checking
 * @param user User we are checking if feature is enabled for them.
 */
const featureIsEnabledForUser = (
  appConfig: AppConfig,
  featurePath: string,
  user: Pick<User, "rbac">,
): featurePath is FeatureFlagPath => {
  if (isFeatureFlagPath(featurePath, appConfig)) {
    const feature = appConfig[featurePath];
    return (
      feature.enabled &&
      (feature.groups.includes("all-users") ||
        (user.rbac.isDeveloper && feature.groups.includes("developers")) ||
        (user.rbac.isInTestGroup && feature.groups.includes("test-group")))
    );
  }
  return false;
};

const addAbilities = (
  builder: AbilityBuilder<AppAbility>,
  appConfig: AppConfig,
  user: Pick<User, "rbac">,
) => {
  for (const [featurePath, value] of Object.entries(appConfig)) {
    if (isFeatureFlag(value)) {
      if (featureIsEnabledForUser(appConfig, featurePath, user)) {
        builder.can("use", featurePath);
      }
    }
  }
};

export const defineAbilityFor = (
  user?: Pick<User, "rbac"> | null,
  appConfig?: AppConfig,
  featureToggleOverrideGroups?: FeatureFlagGroup[],
): AppAbility => {
  const builder = new AbilityBuilder(
    createMongoAbility as CreateAbility<AppAbility>,
  );

  if (appConfig) {
    const modifiedAppConfig =
      user?.rbac.isDeveloper &&
      featureToggleOverrideGroups &&
      mapObject(appConfig, (featureToggleEntry) => {
        const featureToggleContainsOverrideGroups =
          featureToggleEntry.groups.some((group) =>
            featureToggleOverrideGroups.includes(group),
          );

        return {
          enabled:
            featureToggleEntry.enabled && featureToggleContainsOverrideGroups,
          groups: ["developers"] satisfies FeatureFlagGroup[],
        };
      });

    const usedAppConfig = modifiedAppConfig || appConfig;

    if (user) {
      addAbilities(builder, usedAppConfig, user);
    }
  }

  return builder.build();
};

const mapObject = <T extends Record<string, T[keyof T]>>(
  obj: T,
  mapper: (value: T[keyof T], key?: keyof T) => T[keyof T],
) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, mapper(value, key)]),
  ) as T;
